import { createActiveOrderIndexFirstPart, createBestByQuoteActiveIndexFirstPart, createBestByQuoteIndexFirstPart, createDateIndexFirstPart, createL1TxInfoFirstPart, createOrderKey, createUniquenessFirstPart, createUserIndexFirstPart, } from './shared/keys';
/**
 * Retrieves all active order IDs from the C1 (dex-app.cm) contract.
 *
 * Fetches order IDs from the active order index, which contains all orders that are
 * currently active (not cancelled or expired). This is commonly used to display
 * available orders in the marketplace or to process active orders.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 *
 * @returns A promise that resolves to an array of order ID strings. Returns an empty
 *   array if no active orders are found.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getActiveOrderIds } from 'dex-app.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * // Get all active order IDs
 * const activeOrderIds = await getActiveOrderIds(client);
 * console.log(`Found ${activeOrderIds.length} active orders`);
 *
 * // Then fetch full order details
 * const orders = await Promise.all(
 *   activeOrderIds.map(id => getOrderById(client, id))
 * );
 * ```
 */
export const getActiveOrderIds = async (client) => {
    const claimsResponse = await client.fetchClaims(createActiveOrderIndexFirstPart(), null);
    return claimsResponse.map(({ content }) => content.key.second_part[1]);
};
/**
 * Retrieves order IDs sorted by best exchange rate.
 *
 * Fetches order IDs from the best-by-quote index, which contains all orders sorted
 * by their exchange rate. Orders with better rates appear first. This index
 * includes both active and inactive orders.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 *
 * @returns A promise that resolves to an array of order ID strings, sorted by quote
 *   (best rates first). Returns an empty array if no orders are found.
 *
 * @example
 * ```typescript
 * import { type  Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getBestOrderIds, getOrderById } from 'dex-app.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * // Get order IDs sorted by best quote
 * const bestOrderIds = await getBestOrderIds(client);
 *
 * // Get the top 5 orders with best rates
 * const topOrders = await Promise.all(
 *   bestOrderIds.slice(0, 5).map(id => getOrderById(client, id))
 * );
 * ```
 */
export const getBestOrderIds = async (client) => {
    const claimsResponse = await client.fetchClaims(createBestByQuoteIndexFirstPart(), null);
    return claimsResponse.map(({ content }) => content.key.second_part[1]);
};
/**
 * Retrieves active order IDs sorted by best exchange rate.
 *
 * Fetches order IDs from the best-by-quote active index, which contains only active
 * orders sorted by their exchange rate. This combines the benefits of both active
 * filtering and rate-based sorting, making it ideal for displaying the best available
 * orders in the marketplace.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 *
 * @returns A promise that resolves to an array of active order ID strings, sorted by
 *   exchange rate (best rates first). Returns an empty array if no active orders are found.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getBestActiveOrderIds, getOrderById } from 'dex-app.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * // Get best active orders for a swap interface
 * const bestActiveIds = await getBestActiveOrderIds(client);
 * const bestOrders = await Promise.all(
 *   bestActiveIds.map(id => getOrderById(client, id))
 * );
 *
 * // Display the best available swap option
 * if (bestOrders.length > 0) {
 *   console.log(`Best rate: ${bestOrders[0].baseAmount} / ${bestOrders[0].l1Amount}`);
 * }
 * ```
 */
export const getBestActiveOrderIds = async (client) => {
    const claimsResponse = await client.fetchClaims(createBestByQuoteActiveIndexFirstPart(), null);
    return claimsResponse.map(({ content }) => content.key.second_part[1]);
};
/**
 * Retrieves order IDs sorted by creation date (most recent first).
 *
 * Fetches order IDs from the date index, which contains all orders sorted by their
 * creation timestamp. The most recently created orders appear first. This is useful
 * for displaying order history, recent activity, or chronological listings.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 *
 * @returns A promise that resolves to an array of order ID strings, sorted by creation
 *   date (newest first). Returns an empty array if no orders are found.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getLastOrderIds, getOrderById } from 'dex-app.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * // Get the 10 most recently created orders
 * const recentOrderIds = await getLastOrderIds(client);
 * const recentOrders = await Promise.all(
 *   recentOrderIds.slice(0, 10).map(id => getOrderById(client, id))
 * );
 *
 * // Display order history
 * recentOrders.forEach(order => {
 *   console.log(`Order ${order.id} created at ${new Date(order.createdAt)}`);
 * });
 * ```
 */
export const getLastOrderIds = async (client) => {
    const claimsResponse = await client.fetchClaims(createDateIndexFirstPart(), null);
    return claimsResponse.map(({ content }) => content.key.second_part[1]);
};
/**
 * Retrieves all order IDs created by a specific user (market maker).
 *
 * Fetches order IDs from the user index, which contains all orders associated with
 * a particular user. This is used to display a user's order history, manage their
 * active positions, or calculate statistics for a specific market maker.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 * @param user - The user object identifying the market maker. Typically contains
 *   authentication information and a public key payload.
 *
 * @returns A promise that resolves to an array of order ID strings belonging to the
 *   specified user. Returns an empty array if the user has no orders.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getUserOrderIds, getOrderById } from 'dex-app.cm';
 * import { User } from '@coinweb/contract-kit';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * const user: User = {
 *   auth: 'EcdsaContract',
 *   payload: '0x1234...' // Public key
 * };
 *
 * // Get all orders for a user
 * const userOrderIds = await getUserOrderIds(client, user);
 * console.log(`User has ${userOrderIds.length} orders`);
 *
 * // Fetch full order details
 * const userOrders = await Promise.all(
 *   userOrderIds.map(id => getOrderById(client, id))
 * );
 *
 * // Filter active orders
 * const activeOrders = userOrders.filter(
 *   order => order.activityStatus === 'ACTIVE'
 * );
 * ```
 */
export const getUserOrderIds = async (client, user) => {
    const claimsResponse = await client.fetchClaims(createUserIndexFirstPart(user), null);
    return claimsResponse.map(({ content }) => content.key.second_part[1]);
};
/**
 * Retrieves all UTXOs (Unspent Transaction Outputs) that are currently in use by orders.
 *
 * Fetches UTXO information from the uniqueness index, which tracks which Bitcoin-like
 * chain UTXOs are locked in active orders. This prevents double-spending and ensures
 * that UTXOs used in orders cannot be used elsewhere. Essential for BTC/LTC/DOGE
 * transaction validation and UTXO management.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 *
 * @returns A promise that resolves to an array of objects containing `l1TxId` (transaction
 *   ID) and `vout` (output index) for each UTXO currently locked in an order. Returns an
 *   empty array if no UTXOs are in use.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getUtxosInUse } from 'dex-app.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * // Get all UTXOs currently locked in orders
 * const utxosInUse = await getUtxosInUse(client);
 * console.log(`${utxosInUse.length} UTXOs are locked in orders`);
 *
 * // Check if a specific UTXO is available
 * const isUtxoAvailable = (txId: string, vout: number) => {
 *   return !utxosInUse.some(
 *     utxo => utxo.l1TxId === txId && utxo.vout === vout
 *   );
 * };
 *
 * // Filter available UTXOs when creating a new order
 * const availableUtxos = myUtxos.filter(
 *   utxo => isUtxoAvailable(utxo.txId, utxo.vout)
 * );
 * ```
 */
export const getUtxosInUse = async (client) => {
    const claimsResponse = await client.fetchClaims(createUniquenessFirstPart(), null);
    return claimsResponse.map(({ content: { key } }) => {
        const [l1TxId, vout] = key.second_part;
        return {
            l1TxId,
            vout,
        };
    });
};
/**
 * Retrieves full order data by order ID.
 *
 * Fetches the complete order state from the contract for a specific order ID. This is
 * the primary function for getting detailed order information including amounts, status,
 * chain data, and metadata. The function converts hex-encoded big integers to native
 * BigInt values for easier manipulation.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 * @param id - The unique identifier of the order to retrieve.
 *
 * @returns A promise that resolves to an `OrderData` object containing:
 *   - `id`: The order identifier
 *   - `baseAmount`: Amount in base currency (CWEB) as BigInt
 *   - `l1Amount`: Amount in L1 currency as BigInt
 *   - `minL1Amount`: Minimum acceptable L1 amount as BigInt
 *   - `funds`: Available funds for the order as BigInt
 *   - `recipient`: Recipient address for the order
 *   - `createdAt`: Creation timestamp
 *   - `expirationDate`: Order expiration timestamp
 *   - `activityStatus`: Current activity status (e.g., 'ACTIVE', 'CANCELLED')
 *   - `paymentStatus`: Current payment status
 *   - `chainData`: Chain-specific data (e.g., BTC UTXO info)
 *   - `txId`: Associated transaction ID
 *   - `error`: Error message if any
 *   - `history`: Order history access information
 *   - `marketMaker`: User who created the order (null for ownerless orders)
 *
 * @throws {Error} Throws an error with message 'C1 order state not found' if the order
 *   does not exist or cannot be retrieved.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getOrderById, getActiveOrderIds } from 'dex-app.cm';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * enum ACTIVITY_STATUS {
 *   ACTIVE = 'ACTIVE',
 *   COMPLETED = 'COMPLETED',
 *   CANCELLING = 'CANCELLING',
 *   CANCELLED = 'CANCELLED',
 *   EXPIRED = 'EXPIRED',
 *   ERROR = 'ERROR',
 * };

 * type HistoryAccess = {
 *   historyId: string;
 *   timestamp?: number;
 * };
 *
 * type OrderData = {
 *   id: string;
 *   baseAmount: bigint;
 *   l1Amount: bigint;
 *   minL1Amount: bigint;
 *   recipient: string;
 *   createdAt: number;
 *   activityStatus: ACTIVITY_STATUS;
 *   funds: bigint;
 *   chainData: unknown;
 *   txId: string;
 *   error: string | null;
 *   expirationDate: number;
 *   history: HistoryAccess;
 *   marketMaker: User | null;
 * };
 *
 * // Get a specific order
 * try {
 *   const order: OrderData = await getOrderById(client, 'order-123');
 *   console.log(`Order ${order.id}:`);
 *   console.log(`  Base: ${order.baseAmount}`);
 *   console.log(`  L1: ${order.l1Amount}`);
 *   console.log(`  Status: ${order.activityStatus}`);
 *   console.log(`  Market Maker: ${order.marketMaker?.payload || 'None'}`);
 * } catch (error) {
 *   console.error('Order not found:', error);
 * }
 *
 * // Get multiple orders efficiently
 * const orderIds = await getActiveOrderIds(client);
 * const orders = await Promise.all(
 *   orderIds.map(id => getOrderById(client, id))
 * );
 *
 * // Filter orders by criteria
 * const profitableOrders = orders.filter(
 *   order => order.baseAmount > order.l1Amount * 2n
 * );
 * ```
 */
export const getOrderById = async (client, id) => {
    const key = createOrderKey(id);
    const claimResponse = (await client.fetchClaims(key.first_part, key.second_part))[0];
    if (!claimResponse) {
        throw new Error('C1 order state not found');
    }
    const data = claimResponse.content.body;
    return {
        id,
        baseAmount: BigInt(data.baseAmount),
        l1Amount: BigInt(data.l1Amount),
        minL1Amount: BigInt(data.minL1Amount),
        recipient: data.recipient,
        createdAt: data.createdAt,
        activityStatus: data.activityStatus,
        funds: BigInt(data.funds),
        chainData: data.chainData,
        txId: data.txId,
        error: data.error,
        expirationDate: data.expirationDate,
        history: data.history,
        marketMaker: data.marketMaker,
        used: data.used,
        maxUses: data.maxUses,
    };
};
/**
 * Retrieves L1 transaction information for a specific market-maker.
 *
 * Fetches swap L1 transaction data associated with market-maker's orders.
 *
 * @param client - The offchain client instance used to fetch claims from the contract.
 *   Must have a `fetchClaims` method that queries the contract's claim storage.
 * @param user - The user object identifying the market-maker whose L1 transaction info should be
 *   retrieved.
 *
 * @returns A promise that resolves to an array of `L1TxInfoData` objects, each containing:
 *   - `l1TxId`: The L1 transaction ID
 *   - `recipientAddress`: Address that received funds in the L1 transaction
 *   - `backPayAddress`: Fallback address for returning funds
 *   - `amount`: Transaction amount as a hex-encoded big integer
 *   - `l2TxId`: Associated L2 (Coinweb) transaction ID
 *
 *   Returns an empty array if the user has no L1 transaction records.
 *
 * @example
 * ```typescript
 * import { type Client } from '@pactswap/cwap-cm-lib/offchain';
 * import { getL1TxInfo } from 'dex-app.cm';
 * import { User } from '@coinweb/contract-kit';
 *
 * const client: Client = {
 *   fetchClaims: async (firstPart, secondPart) => {
 *     // Implementation using a client library like '@coinweb/wallet-lib' or '@coinweb/claims-client'
 *   }
 * };
 *
 * const user: User = {
 *   auth: 'EcdsaContract',
 *   payload: '0x1234...' // Public key
 * };
 *
 * type L1TxInfoData = {
 *   l1TxId: string;
 *   recipientAddress: string;
 *   backPayAddress: string;
 *   amount: HexBigInt;
 *   l2TxId: string;
 * };
 *
 * // Get user's L1 transaction history
 * const l1TxInfos: L1TxInfoData[] = await getL1TxInfo(client, user);
 * console.log(`Here are ${l1TxInfos.length} L1 transactions`);
 *
 * // Display transaction details
 * l1TxInfos.forEach(txInfo => {
 *   console.log(`L1 TX: ${txInfo.l1TxId}`);
 *   console.log(`  Amount: ${txInfo.amount}`);
 *   console.log(`  Recipient: ${txInfo.recipientAddress}`);
 *   console.log(`  L2 TX: ${txInfo.l2TxId}`);
 * });
 * ```
 */
export const getL1TxInfo = async (client, user) => {
    const claimsResponse = await client.fetchClaims(createL1TxInfoFirstPart(user), null);
    return claimsResponse.map((claim) => claim.content.body);
};
